import { Component, ElementRef, Input, ViewChild, OnDestroy } from '@angular/core';
import { Chart, ChartItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { abbreviateNumber } from 'src/app/shared/helpers';
import { calcAutoFontSize } from 'src/app/shared/helpers';

// Chart palette:
// - Primary:   SI-Blue-500      #3F6BB4
// - Secondary: Princeton-500    #FF994E
// - Tertiary:  Turquoise-500    #43D1DB
const COLORS = ['#3F6BB4', '#FF994E', '#43D1DB'];

@Component({
    selector: 'app-line-chart',
    template: `<div class='chart-wrapper'><canvas #lineChart></canvas></div>`,
    styleUrls: ['./line-chart.component.scss'],
    standalone: false
})
export class LineChartComponent implements OnDestroy {
    @Input()
    set chartLabels(data: string[]) {
        this._chartLabels = data;
        this.createChart();
    };

    @Input()
    set chartData(data: Record<string, number[]>) {
        this._chartData = data;
        this.createChart();
    };

    @Input()
    set autoAxisLabelFontSize(data: boolean) {
        this._autoAxisLabelFontSize = data;
        this.createChart();
    };

    @Input()
    set axisLabelFontSize(data: number) {
        this._axisLabelFontSize = data;
        this.createChart();
    };

    @Input() legendKeyToHide: string;

    @ViewChild('lineChart', { static: true }) lineChartCanvas: ElementRef<HTMLCanvasElement>;

    private _chart: Chart = null;
    private _chartLabels: string[];
    private _chartData: Record<string, number[]>;
    private _axisLabelFontSize: number = 10.6;
    private _autoAxisLabelFontSize: boolean = false;

    createChart() {
        if (!this.lineChartCanvas || !this._chartLabels || !this._chartData) {
            return
        }
        this.destroyChart();
        const context: ChartItem = this.lineChartCanvas.nativeElement.getContext('2d');
        const isAllZero = Object.values(this._chartData).reduce((acc, arr) => acc.concat(arr), []).every(val => val === 0);
        const axisLabelFontSize = this._axisLabelFontSize;
        const calcFontSize = this._autoAxisLabelFontSize ? () => calcAutoFontSize() : () => axisLabelFontSize;

        if (context) {
            this._chart = new Chart(context, {
                type: 'line',
                plugins: [ChartDataLabels],
                data: {
                    labels: this._chartLabels,
                    datasets: this.setDataSet(),
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            labels: {
                                usePointStyle: false,
                                boxWidth: 8,
                                boxHeight: 2,
                                font: { weight: 'bold' },
                            },
                            onClick: (e, legendItem, legend) => {
                                const chart = legend.chart;
                                const index = legendItem.datasetIndex;
                                const visibleDatasets = chart.data.datasets.filter(
                                (_, i) => chart.isDatasetVisible(i)
                                ).length;
                                if (visibleDatasets > 1 || !chart.isDatasetVisible(index)) {
                                    chart.setDatasetVisibility(index, !chart.isDatasetVisible(index));
                                    chart.update();
                                }
                            }
                        },
                        tooltip: { enabled: false },
                        datalabels: {
                            display: (context) => context.active ? 'auto' : false,
                            font: { weight: 'normal', size: 14 },
                            align: 'top',
                            color: (context) => {
                                return context.dataset.borderColor as string;
                            },
                            offset: -2,
                            formatter: (value) => abbreviateNumber(value)

                        }
                    },
                    interaction: {
                        mode: 'dataset',
                        intersect: false
                    },
                    elements: {
                        point: {
                            hoverRadius: 10,
                            hitRadius: 15,
                        }
                    },
                    hover: {
                        mode: 'dataset',
                        intersect: false
                    },
                    scales: {
                        x: {
                            offset: true,
                            grid: {
                                display: false,
                            },
                            ticks: {
                                padding: 5,
                                font: {
                                    size: calcFontSize
                                }
                            },
                        },
                        y: {
                            beginAtZero: true,
                            max: this.getMax(),
                            ticks: {
                                stepSize: isAllZero ? 0.1 : null,
                                callback: (value: number) => Number.isInteger(value) ? abbreviateNumber(value) : '',
                                font: {
                                    size: calcFontSize
                                }
                            }
                        }
                    }
                }
            });
            if(this.legendKeyToHide) {
                const index: number = this._chart.data.datasets.findIndex(ds => ds.label === this.legendKeyToHide);
                this.toggleLegendItem(index)
            }
        }
    }

    ngOnDestroy(): void {
        this.destroyChart();
    }

    toggleLegendItem(index: number) {
        const meta = this._chart.getDatasetMeta(index);
        meta.hidden = meta.hidden === null ? !this._chart.data.datasets[index]?.hidden : null;
        this._chart.update();
    }

    private destroyChart() {
        if (this._chart) {
            this._chart.destroy();
            this._chart = null;
        }
    }

    private setDataSet(): any[] {
        return Object.keys(this._chartData).map((key: string, index: number) => ({
            label: key?.charAt(0)?.toUpperCase() + key?.slice(1),
            data: this._chartData[key],
            borderColor: COLORS?.[index],
            backgroundColor: COLORS?.[index],
            borderWidth: 1.5,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0
        }));
    }

    private getMax(): number {
        const allValues = Object.values(this._chartData).reduce((acc, val) => acc.concat(val), []);
        const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;

        if (maxValue === 0) return 1;

        const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
        const stepSize = magnitude;
        const roundedMax = Math.ceil(maxValue / stepSize) * stepSize;

        return roundedMax + stepSize;
    }

}
